import { fillMissingElevations, sampleForDisplay, titleFromFileName, fingerprint } from "./ride-import-utils";
import type { ParsedFit } from "fit-file-parser";
import { validateFitEnvelope } from "./fit-envelope";
import { calculateRideMetrics, type Ride } from "./ride";
import { browserUuid } from './browser-crypto';
import { fitTimerDuration } from './fit-timer';

const MAXIMUM_PARSED_RECORDS = 100_000;
const MAXIMUM_DISPLAY_POINTS = 12_000;

type FitRecord = NonNullable<ParsedFit["records"]>[number];

/**
 * Parses an untrusted FIT file locally and converts it into the ride domain.
 *
 * @param file - Browser-selected FIT file.
 * @returns Validated ride suitable for synchronized map playback.
 * @throws Error when the file is oversized, malformed, corrupt, or lacks a GPS activity.
 */
export async function parseFitFile(file: File): Promise<Ride> {
  if (!file.name.toLowerCase().endsWith(".fit")) {
    throw new Error("Select a file with the .fit extension.");
  }
  const content = await file.arrayBuffer();
  validateFitEnvelope(new Uint8Array(content));

  // Dynamic loading keeps the parser out of the initial production bundle.
  const [{ default: FitParser }, { extractRecordingDevice }] = await Promise.all([
    import("fit-file-parser"),
    import("./fit-recording-device"),
  ]);
  let parsed: ParsedFit;
  try {
    parsed = await new FitParser({ force: false, lengthUnit: "m", mode: "list" })
      .parseAsync(content);
  } catch {
    throw new Error("The FIT parser rejected the file integrity or structure.");
  }

  const ride = normalizeFitRecords(parsed.records ?? [], titleFromFileName(file.name), fitTimerDuration(parsed));
  const sourceFingerprint = await fingerprint(content);
  const recordingDevice = extractRecordingDevice(parsed);
  return { ...ride, sourceFingerprint, sourceFileName: file.name,
    ...(recordingDevice ? { recordingDevice } : {}) };
}

/**
 * Converts parsed FIT records into bounded, validated ride points.
 *
 * @param records - FIT record messages in file order.
 * @param title - Sanitized display title.
 * @param timerDurationSeconds - Device-reported activity timer, when available.
 * @returns Imported ride with full-resolution metrics and bounded display points.
 * @throws Error when the activity exceeds limits or lacks usable GPS, time, or elevation data.
 */
export function normalizeFitRecords(records: readonly FitRecord[], title: string, timerDurationSeconds?: number): Ride {
  if (records.length > MAXIMUM_PARSED_RECORDS) {
    throw new Error("The FIT activity contains too many records.");
  }

  const candidates = records.flatMap((record) => {
    if (
      !Number.isFinite(record.position_long)
      || !Number.isFinite(record.position_lat)
      || !(record.timestamp instanceof Date)
      || !Number.isFinite(record.timestamp.getTime())
    ) {
      return [];
    }
    const elevation = Number.isFinite(record.enhanced_altitude)
      ? record.enhanced_altitude!
      : Number.isFinite(record.altitude)
        ? record.altitude!
        : undefined;
    return [{
      elevation,
      latitude: record.position_lat!,
      longitude: record.position_long!,
      recordedAt: record.timestamp.toISOString(),
    }];
  });

  if (candidates.length < 2) {
    throw new Error("The FIT activity does not contain enough timed GPS records.");
  }
  const points = fillMissingElevations(candidates);
  const metrics = calculateRideMetrics(points, timerDurationSeconds);

  return {
    id: browserUuid(),
    metrics,
    ...(timerDurationSeconds === undefined ? {} : { timerDurationSeconds }),
    points: sampleForDisplay(points, MAXIMUM_DISPLAY_POINTS),
    sourcePoints: points,
    region: "Imported FIT activity",
    source: "fit",
    title: title || "Imported FIT trace",
  };
}
